import * as z from 'zod';

export const categorySchema = z.strictObject({
  id: z.string(),
  name: z.string().min(1),
  color: z.string().optional(),
});

export const fixedCostSchema = z.strictObject({
  id: z.string(),
  label: z.string().min(1),
  amount: z.number().min(0),
  categoryId: z.string(),
  occursAtMonth: z.number().int().min(1),
});

export const recurringCostSchema = z.strictObject({
  id: z.string(),
  label: z.string().min(1),
  monthlyAmount: z.number().min(0),
  categoryId: z.string(),
});

export const tierSchema = z.strictObject({
  id: z.string(),
  name: z.string().min(1),
  monthlyPrice: z.number().min(0),
  entryPrice: z.number().min(0).optional(),
  entryCost: z.number().min(0).optional(),
  monthlyChurnRate: z.number().min(0).max(1),
  acquisitionShare: z.number().min(0).max(1),
  costPerClientPerMonth: z.number().min(0).optional(),
});

const ACQUISITION_SHARE_SUM_TOLERANCE = 0.01;

const headcountRampSchema = z
  .strictObject({
    enabled: z.boolean(),
    targetHeadcount: z.number().min(0),
  })
  .optional();

export const simulatorStateSchema = z
  .strictObject({
    version: z.string(),
    currency: z.literal('EUR'),
    info: z
      .strictObject({
        title: z.string().optional(),
        description: z.string().optional(),
      })
      .optional(),
    initialDeficit: z.number().min(0).optional(),
    team: z.strictObject({
      headcount: z.number().min(0), // fractions d'ETP autorisées (ex : 0.5)
      avgAnnualCostPerFTE: z.number().min(0),
      chargesMultiplier: z.number().positive().optional(),
      headcountRamp: headcountRampSchema,
    }),
    categories: z.array(categorySchema),
    fixedCosts: z.array(fixedCostSchema),
    recurringCosts: z.array(recurringCostSchema),
    tiers: z.array(tierSchema).min(1).max(10),
    acquisition: z.strictObject({
      newClientsPerMonth: z.number().min(0),
      model: z.enum(['constant', 'linear-ramp', 's-curve']).optional(),
      rampUpMonths: z.number().int().min(0).max(60).optional(),
      discrete: z.boolean().optional(),
      // Un cycle de vente ≥ horizon est légal : il modélise un pipeline qui ne facture
      // jamais dans la fenêtre de projection (burn d'avant-vente sans revenu).
      salesCycleMonths: z.number().int().min(0).max(60).optional(),
      winRate: z.number().gt(0).max(1).optional(),
      presalesCostPerDeal: z.number().min(0).optional(),
    }),
    projection: z.strictObject({
      horizonMonths: z.number().int().min(3).max(60),
    }),
  })
  .superRefine((state, ctx) => {
    const shareSum = state.tiers.reduce((sum, tier) => sum + tier.acquisitionShare, 0);
    if (Math.abs(shareSum - 1) > ACQUISITION_SHARE_SUM_TOLERANCE) {
      ctx.addIssue({
        code: 'custom',
        message: `La somme des parts d'acquisition doit être égale à 100% (actuellement ${Math.round(shareSum * 100)}%).`,
        path: ['tiers'],
      });
    }

    for (const [index, fixedCost] of state.fixedCosts.entries()) {
      if (fixedCost.occursAtMonth > state.projection.horizonMonths) {
        ctx.addIssue({
          code: 'custom',
          message: `Le mois d'occurrence (${fixedCost.occursAtMonth}) dépasse l'horizon de projection (${state.projection.horizonMonths} mois).`,
          path: ['fixedCosts', index, 'occursAtMonth'],
        });
      }
    }
  });

export type SimulatorStateInput = z.infer<typeof simulatorStateSchema>;
